import type { AuthError, Session } from '@supabase/supabase-js';
import { supabase } from '../SupabaseClient/supabase-client';
import type { ApiClient, ApiError, AuthSession } from './types';

const mapSession = (session: Session | null): AuthSession | null => {
  if (!session) {
    return null;
  }

  return {
    userId: session.user.id,
    email: session.user.email,
    accessToken: session.access_token ?? null,
    refreshToken: session.refresh_token ?? null,
  };
};

const mapError = (error: AuthError | null): ApiError | null => {
  if (!error) {
    return null;
  }

  return { message: error.message, code: error.code };
};

export const createSupabaseApiClient = (): ApiClient => ({
  auth: {
    async getSession() {
      const { data } = await supabase.auth.getSession();
      return mapSession(data.session ?? null);
    },

    async signIn(params) {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: params.email,
        password: params.password,
      });

      return {
        session: mapSession(data.session ?? null),
        error: mapError(error),
      };
    },

    async signUp(params) {
      const { error } = await supabase.auth.signUp({
        email: params.email,
        password: params.password,
        options: {
          data: params.fullName ? { full_name: params.fullName } : undefined,
          emailRedirectTo: params.redirectTo,
        },
      });

      return {
        error: mapError(error),
      };
    },

    async signOut() {
      await supabase.auth.signOut();
    },

    onAuthStateChange(callback) {
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange((_, session) => {
        callback(mapSession(session));
      });

      return () => subscription.unsubscribe();
    },
  },
});
